import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Theme, Content, Loading } from '@carbon/react';
import { AuthProvider, useAuth } from '@/lib/auth/auth-context';
import { getFontScale, setFontScale, type FontScale } from '@/lib/prefs/fontScale';
import AppHeader from '@/components/layout/AppHeader';
import KnowledgeManagementHomePage from '@/pages/KnowledgeManagementHomePage';
import HomePage from '@/pages/HomePage';
import OntologyEditorPage from '@/pages/OntologyEditorPage';
import SettingsPage from '@/pages/SettingsPage';
import UsagePage from '@/pages/UsagePage';
import KnowledgeBasesPage from '@/pages/KnowledgeBasesPage';
import KnowledgeBaseDetailPage from '@/pages/KnowledgeBaseDetailPage';
import KnowledgeObjectDetailPage from '@/pages/KnowledgeObjectDetailPage';
import { AuthPage } from '@/pages/AuthPage';
import { authRouteFor, AuthCallbackPage } from '@/pages/AuthCallbackPage';
import { AdminPage } from '@/pages/AdminPage';
import NotFoundPage from '@/pages/NotFoundPage';
import KnowledgeExplorerListPage from '@/pages/KnowledgeExplorerListPage';
import KnowledgeExplorerPage from '@/pages/KnowledgeExplorerPage';
import DatasetsListPage from '@/pages/DatasetsListPage';
import { DatasetDetailPage } from '@/pages/DatasetDetailPage';

type AppTheme = 'g10' | 'g100';

/**
 * Protected route wrapper — sends an unauthenticated visitor to /auth AND REMEMBERS WHERE THEY WERE
 * GOING.
 *
 * This bare `Navigate to="/auth"` was the first of three places a deep link died (measured 2026-08-19).
 * The sequence was: /editor renders, this guard replaces the URL with /auth, the boot effect then starts
 * the silent sign-in FROM /auth, so the route recorded to come back to was /auth, which the callback
 * refuses on purpose — and every bookmark, every link in a report and eleven e2e specs landed on the
 * home page instead. The redirect now carries the requested path, and `authRouteFor` decides what is
 * safe to carry (in-app paths only, never an auth route, never an absolute URL).
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Loading withOverlay={false} description="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={authRouteFor(`${location.pathname}${location.search}${location.hash}`)} replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  const [theme, setTheme] = useState<AppTheme>('g100');
  const [fontScale, setFontScaleState] = useState<FontScale>(getFontScale());

  useEffect(() => {
    const savedTheme = window.localStorage.getItem('app-theme');
    if (savedTheme === 'g10' || savedTheme === 'g100') {
      setTheme(savedTheme);
    }
  }, []);

  const handleThemeToggle = () => {
    setTheme((currentTheme) => {
      const nextTheme = currentTheme === 'g100' ? 'g10' : 'g100';
      window.localStorage.setItem('app-theme', nextTheme);
      return nextTheme;
    });
  };

  const handleFontScaleChange = (scale: FontScale) => {
    setFontScale(scale);
    setFontScaleState(scale);
  };

  return (
    <Theme theme={theme}>
      <Routes>
        {/* Public routes */}
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />

        {/* Protected routes with IBM header */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AppHeader theme={theme} onToggleTheme={handleThemeToggle} fontScale={fontScale} onFontScaleChange={handleFontScaleChange} />
              <Content className="app-content">
                <KnowledgeManagementHomePage />
              </Content>
            </ProtectedRoute>
          }
        />
        <Route
          path="/ontology-builder"
          element={
            <ProtectedRoute>
              <AppHeader theme={theme} onToggleTheme={handleThemeToggle} fontScale={fontScale} onFontScaleChange={handleFontScaleChange} />
              <Content className="app-content">
                <HomePage />
              </Content>
            </ProtectedRoute>
          }
        />
        <Route
          path="/editor/:id?"
          element={
            <ProtectedRoute>
              <AppHeader theme={theme} onToggleTheme={handleThemeToggle} fontScale={fontScale} onFontScaleChange={handleFontScaleChange} />
              <Content className="app-content app-content--full">
                <OntologyEditorPage theme={theme} onToggleTheme={handleThemeToggle} />
              </Content>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <AppHeader theme={theme} onToggleTheme={handleThemeToggle} fontScale={fontScale} onFontScaleChange={handleFontScaleChange} />
              <Content className="app-content">
                <SettingsPage />
              </Content>
            </ProtectedRoute>
          }
        />
        <Route
          path="/usage"
          element={
            <ProtectedRoute>
              <AppHeader theme={theme} onToggleTheme={handleThemeToggle} fontScale={fontScale} onFontScaleChange={handleFontScaleChange} />
              <Content className="app-content">
                <UsagePage />
              </Content>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AppHeader theme={theme} onToggleTheme={handleThemeToggle} fontScale={fontScale} onFontScaleChange={handleFontScaleChange} />
              <Content className="app-content">
                <AdminPage />
              </Content>
            </ProtectedRoute>
          }
        />
        <Route
          path="/knowledge-bases"
          element={
            <ProtectedRoute>
              <AppHeader theme={theme} onToggleTheme={handleThemeToggle} fontScale={fontScale} onFontScaleChange={handleFontScaleChange} />
              <Content className="app-content">
                <KnowledgeBasesPage />
              </Content>
            </ProtectedRoute>
          }
        />
        <Route
          path="/knowledge-bases/:id"
          element={
            <ProtectedRoute>
              <AppHeader theme={theme} onToggleTheme={handleThemeToggle} fontScale={fontScale} onFontScaleChange={handleFontScaleChange} />
              <Content className="app-content">
                <KnowledgeBaseDetailPage />
              </Content>
            </ProtectedRoute>
          }
        />
        <Route
          path="/knowledge-bases/:kbId/objects/:koId"
          element={
            <ProtectedRoute>
              <AppHeader theme={theme} onToggleTheme={handleThemeToggle} fontScale={fontScale} onFontScaleChange={handleFontScaleChange} />
              <Content className="app-content app-content--full">
                <KnowledgeObjectDetailPage />
              </Content>
            </ProtectedRoute>
          }
        />

        <Route
          path="/explorer"
          element={
            <ProtectedRoute>
              <AppHeader theme={theme} onToggleTheme={handleThemeToggle} fontScale={fontScale} onFontScaleChange={handleFontScaleChange} />
              <KnowledgeExplorerListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/explorer/:id"
          element={
            <ProtectedRoute>
              <AppHeader theme={theme} onToggleTheme={handleThemeToggle} fontScale={fontScale} onFontScaleChange={handleFontScaleChange} />
              <KnowledgeExplorerPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/datasets"
          element={
            <ProtectedRoute>
              <AppHeader theme={theme} onToggleTheme={handleThemeToggle} fontScale={fontScale} onFontScaleChange={handleFontScaleChange} />
              <DatasetsListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/datasets/:id"
          element={
            <ProtectedRoute>
              <AppHeader theme={theme} onToggleTheme={handleThemeToggle} fontScale={fontScale} onFontScaleChange={handleFontScaleChange} />
              <DatasetDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Scientific Agent moved to its own module (snxt-platform-scientific-agent-ui,
            scientificagent.science-next.io). Removed from this app. */}

        {/* Redirect old /knowledge-search URL to unified Knowledge Explorer */}
        <Route path="/knowledge-search" element={<Navigate to="/explorer" replace />} />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Theme>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;

// Made with Bob
